import React, { useState, useEffect } from 'react';
import { Landmark, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { eur } from '../ExpenseDetail/DetailKpis';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Row = ({ label, value, strong, testId }) => (
  <div className="flex justify-between text-sm">
    <span className="text-gray-500">{label}</span>
    <span className={strong ? 'font-bold' : 'font-medium text-gray-800'} data-testid={testId}>{value}</span>
  </div>
);

const QuarterCard = ({ q, current }) => {
  const pay = q.result >= 0;
  return (
    <div className={`rounded-xl border p-3 space-y-1.5 ${current ? 'border-gray-900 ring-1 ring-gray-900' : ''}`} data-testid={`vat-quarter-${q.quarter}`}>
      <div className="flex items-center justify-between">
        <span className="font-semibold text-gray-900">{q.label}</span>
        {current && <span className="text-[10px] uppercase tracking-wide bg-gray-900 text-white px-1.5 py-0.5 rounded">actual</span>}
      </div>
      <Row label="IVA ventas" value={eur(q.output_vat)} testId={`vat-output-${q.quarter}`} />
      <Row label="IVA gastos" value={`− ${eur(q.input_vat)}`} testId={`vat-input-${q.quarter}`} />
      <div className="border-t pt-1.5">
        <div className="flex justify-between text-sm">
          <span className="text-gray-700 font-medium">{pay ? 'A pagar' : 'A compensar'}</span>
          <span className={`font-bold ${pay ? 'text-red-600' : 'text-green-600'}`} data-testid={`vat-result-${q.quarter}`}>{eur(Math.abs(q.result))}</span>
        </div>
      </div>
      {q.missing_vat_count > 0 && (
        <p className="text-[11px] text-amber-700 flex items-start gap-1" data-testid={`vat-missing-${q.quarter}`}>
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          {q.missing_vat_count} gasto{q.missing_vat_count > 1 ? 's' : ''} sin IVA anotado ({eur(q.missing_vat_amount)})
        </p>
      )}
    </div>
  );
};

export const VatQuarterly = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/admin/gastos/iva-trimestral?year=${year}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
    }).then(r => (r.ok ? r.json() : null)).then(setData).catch(() => setData(null));
  }, [year]);

  const now = new Date();
  const currentQ = now.getFullYear() === year ? Math.floor(now.getMonth() / 3) + 1 : null;

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden" data-testid="vat-quarterly">
      <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Landmark size={17} /> IVA trimestral (modelo 303)</h3>
        <div className="flex items-center gap-1">
          <button onClick={() => setYear(y => y - 1)} className="p-1 rounded hover:bg-gray-200" data-testid="vat-prev-year"><ChevronLeft size={16} /></button>
          <span className="text-sm font-semibold w-12 text-center" data-testid="vat-year">{year}</span>
          <button onClick={() => setYear(y => y + 1)} className="p-1 rounded hover:bg-gray-200" data-testid="vat-next-year"><ChevronRight size={16} /></button>
        </div>
      </div>
      <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {data?.quarters.map(q => <QuarterCard key={q.quarter} q={q} current={q.quarter === currentQ} />)}
      </div>
      <p className="px-4 pb-3 text-[11px] text-gray-400">
        IVA de ventas calculado al {data?.sales_vat_rate ?? 10}% sobre lo cobrado. Es orientativo: confírmalo con tu gestoría.
      </p>
    </div>
  );
};
